import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useOpenPath } from '../../../hooks/useOpenPath';
import { BaseComponent, Building } from '../../../types/protocols';
import { BaseComponentImpl, RoomImpl } from '../../../types/implementations';
import { BaseType } from '../../../types/Enums';
import { translate } from '../../../types/EnumTranslations';
import BlueprintWithMarkers, { BlueprintHighlight, NumberedMarker, NumberedMarkerGlyph } from '../../../Components/BlueprintWithMarkers';
import ToggleSwitch from '../../../Components/ToggleSwitch';
import { asSatelliteMarkers, DrawingView } from '../../../Helpers/componentDrawing';
import { isSpotRented, parkingAreaDisplayName, parkingSpotDisplayName, spotHasCharger, spotStatusColor, spotTypeIcon } from '../../../Helpers/parkingUtils';
import { STORAGE_UNIT_ICON, storageUnitStatusColor, isUnitRented as isStorageRented, storageUnitDisplayName } from '../../../Helpers/storageUnitUtils';
import { unitStatusColor, unitTypeIcon, effectiveUnitLocations, unitDisplayName, unitMarkerRented } from '../../../Helpers/unitUtils';
import { naturalCompare } from '../../../Helpers/naturalSort';
import {
    DRAWING_LAYERS,
    DrawingLayer,
    DrawingItem,
    DrawingItemKind,
    RentableObjects,
    componentLayer,
    drawingContent,
    drawingId,
    drawingItemPath,
} from './buildingTabsView';

interface DrawingsTabProps {
    building: Building;
    buildingKey: string;
    views: DrawingView[];
    view: DrawingView | null;
    onViewChange: (id: string) => void;
    hiddenLayers: Set<DrawingLayer>;
    onHiddenLayersChange: (hidden: Set<DrawingLayer>) => void;
    objects: RentableObjects;
}

/** One entry of the pane's item list. */
interface ListItem {
    item: DrawingItem;
    label: string;
}

interface ListGroup {
    key: string;
    label: string;
    items: ListItem[];
}

const HIGHLIGHT_KIND: Record<DrawingItemKind, BlueprintHighlight['kind']> = {
    room: 'room',
    component: 'utility',
    spot: 'spot',
    unit: 'spot',
    storage: 'spot',
};

const byLabel = (a: ListItem, b: ListItem) => naturalCompare(a.label, b.label);

const itemId = (item: DrawingItem) => `${item.kind}:${item.key}`;

/**
 * Ritningar: one drawing at a time (satellite image, takplan or a level) with
 * every placed item as a marker, read-only. The pane lists what the drawing
 * shows, grouped, and last what belongs on it but is not placed yet.
 */
const DrawingsTab: React.FC<DrawingsTabProps> = ({
    building,
    buildingKey,
    views,
    view,
    onViewChange,
    hiddenLayers,
    onHiddenLayersChange,
    objects,
}) => {
    const openPath = useOpenPath();
    const [hovered, setHovered] = useState<DrawingItem | null>(null);
    // The marker under the mouse; its row is marked and scrolled into the pane.
    const [hoveredMarker, setHoveredMarker] = useState<DrawingItem | null>(null);
    const paneRef = useRef<HTMLDivElement>(null);
    const model = building.model;
    const content = useMemo(() => (view ? drawingContent(view, model, objects) : null), [view, model, objects]);
    const shows = (layer: DrawingLayer) => !hiddenLayers.has(layer);
    // Components are split over the Komponenter and Fastighetsteknik layers.
    const shownComponents = (components: BaseComponent[]) => components.filter(c => shows(componentLayer(c)));

    const componentImpls = useMemo(() => {
        const map = new Map<string, BaseComponentImpl>();
        content?.components.placed.concat(content.components.unplaced).forEach(c => map.set(c.key, new BaseComponentImpl(c)));
        return map;
    }, [content]);

    const markers = useMemo(() => {
        const empty = { rooms: [] as RoomImpl[], utilities: [] as BaseComponentImpl[], spots: [] as NumberedMarker[], spotKinds: new Map<string, DrawingItemKind>() };
        if (!content || !view?.imageKey) return empty;
        const rooms = shows('rooms') ? content.rooms.placed.map(room => new RoomImpl(room)) : [];
        const placedComponents = shownComponents(content.components.placed).map(c => componentImpls.get(c.key)!);
        const utilities = view.kind === 'satellite' ? asSatelliteMarkers(placedComponents, view.imageKey) : placedComponents;
        const spots: NumberedMarker[] = [];
        const spotKinds = new Map<string, DrawingItemKind>();
        if (shows('parking')) {
            content.spots.placed.forEach(({ spot, area }) => {
                const glyphs: NumberedMarkerGlyph[] = [];
                if (spotHasCharger(spot)) glyphs.push('charger');
                if (spot.hasOutlet === true) glyphs.push('outlet');
                spotKinds.set(spot.key, 'spot');
                spots.push({
                    key: spot.key,
                    number: spot.number,
                    color: spotStatusColor(spot),
                    icon: spotTypeIcon(spot.type),
                    glyphs,
                    isRented: isSpotRented(spot),
                    displayName: `${parkingSpotDisplayName(spot)} (${parkingAreaDisplayName(area)})`,
                    blueprintLocations: spot.blueprintLocations,
                });
            });
        }
        if (shows('rentables')) {
            content.units.placed.forEach(unit => {
                spotKinds.set(unit.key, 'unit');
                spots.push({
                    key: unit.key,
                    number: unit.number,
                    color: unitStatusColor(unit),
                    icon: unitTypeIcon(unit.kind),
                    iconColored: true,
                    isRented: unitMarkerRented(unit),
                    displayName: unitDisplayName(unit),
                    blueprintLocations: effectiveUnitLocations(unit, model.structure?.rooms),
                });
            });
            content.storageUnits.placed.forEach(unit => {
                spotKinds.set(unit.key, 'storage');
                spots.push({
                    key: unit.key,
                    number: unit.number,
                    color: storageUnitStatusColor(unit),
                    icon: STORAGE_UNIT_ICON,
                    iconColored: true,
                    isRented: isStorageRented(unit),
                    displayName: storageUnitDisplayName(unit),
                    blueprintLocations: unit.blueprintLocations,
                });
            });
        }
        return { rooms, utilities, spots, spotKinds };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content, view, componentImpls, hiddenLayers, model.structure?.rooms]);

    /** The pane's list: drawn items grouped per layer, then everything unplaced in one group. */
    const groups = useMemo((): ListGroup[] => {
        if (!content) return [];
        const result: ListGroup[] = [];
        const roomItems = (rooms: typeof content.rooms.placed) =>
            rooms.map(room => ({ item: { kind: 'room' as const, key: room.key }, label: new RoomImpl(room).displayName() }));
        const componentItems = (components: typeof content.components.placed) =>
            components.map(c => ({ item: { kind: 'component' as const, key: c.key }, label: componentImpls.get(c.key)!.displayName(building) }));
        const spotItems = (entries: typeof content.spots.placed) =>
            entries.map(({ spot, area }) => ({ item: { kind: 'spot' as const, key: spot.key }, label: `${parkingSpotDisplayName(spot)} · ${parkingAreaDisplayName(area)}` }));
        const unitItems = (units: typeof content.units.placed) =>
            units.map(unit => ({ item: { kind: 'unit' as const, key: unit.key }, label: unitDisplayName(unit) }));
        const storageItems = (units: typeof content.storageUnits.placed) =>
            units.map(unit => ({ item: { kind: 'storage' as const, key: unit.key }, label: storageUnitDisplayName(unit) }));
        const push = (key: string, label: string, items: ListItem[]) => {
            if (items.length > 0) result.push({ key, label, items: [...items].sort(byLabel) });
        };

        if (shows('rooms')) push('rooms', 'Rum', roomItems(content.rooms.placed));
        const byType = new Map<BaseType, typeof content.components.placed>();
        shownComponents(content.components.placed).forEach(c => byType.set(c.type, [...(byType.get(c.type) ?? []), c]));
        Array.from(byType.entries())
            .sort(([a], [b]) => naturalCompare(translate(a), translate(b)))
            .forEach(([type, components]) => push(`type:${type}`, translate(type), componentItems(components)));
        if (shows('parking')) push('spots', 'Parkeringsplatser', spotItems(content.spots.placed));
        if (shows('rentables')) {
            push('units', 'Lägenheter & lokaler', unitItems(content.units.placed));
            push('storage', 'Förråd', storageItems(content.storageUnits.placed));
        }
        const unplaced = [
            ...(shows('rooms') ? roomItems(content.rooms.unplaced) : []),
            ...componentItems(shownComponents(content.components.unplaced)),
            ...(shows('parking') ? spotItems(content.spots.unplaced) : []),
            ...(shows('rentables') ? [...unitItems(content.units.unplaced), ...storageItems(content.storageUnits.unplaced)] : []),
        ];
        push('unplaced', 'Ej placerade', unplaced);
        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content, componentImpls, building, hiddenLayers]);

    // Scrolls only the pane card, never the page, and only when the row is out of view.
    useEffect(() => {
        const pane = paneRef.current;
        if (!hoveredMarker || !pane) return;
        const id = itemId(hoveredMarker);
        const row = Array.from(pane.querySelectorAll<HTMLElement>('[data-item]')).find(el => el.dataset.item === id);
        if (!row) return;
        const paneBox = pane.getBoundingClientRect();
        const rowBox = row.getBoundingClientRect();
        if (rowBox.top < paneBox.top) pane.scrollTop -= paneBox.top - rowBox.top + 8;
        else if (rowBox.bottom > paneBox.bottom) pane.scrollTop += rowBox.bottom - paneBox.bottom + 8;
    }, [hoveredMarker]);

    if (!view) {
        return <div className="bt-empty">Byggnaden har inga våningsplan, ingen takplan och ingen satellitbild ännu.</div>;
    }

    // Components and utilities are always offered, rooms on a level; the parking and
    // rentable toggles only when the level has something of the kind, placed or not.
    const has = (placement: { placed: unknown[]; unplaced: unknown[] }) => placement.placed.length + placement.unplaced.length > 0;
    const layers = DRAWING_LAYERS.filter(layer => {
        if (layer.key === 'components' || layer.key === 'utilities') return true;
        if (view.kind !== 'level' || !content) return false;
        if (layer.key === 'parking') return has(content.spots);
        if (layer.key === 'rentables') return has(content.units) || has(content.storageUnits);
        return true;
    });
    const toggleLayer = (layer: DrawingLayer) => {
        const next = new Set(hiddenLayers);
        if (next.has(layer)) next.delete(layer);
        else next.add(layer);
        onHiddenLayersChange(next);
    };
    const pagePath = view.kind === 'level'
        ? { label: 'Öppna våningsplan', path: `/building/${buildingKey}/level/${view.levelKey}` }
        : view.kind === 'roofPlan'
            ? { label: 'Öppna takplan', path: `/building/${buildingKey}/roof` }
            : null;
    const highlight = hovered ? { kind: HIGHLIGHT_KIND[hovered.kind], key: hovered.key } : undefined;
    // Every click, on a marker or a row, opens the item's page.
    const pathOf = (item: DrawingItem) => drawingItemPath(item, buildingKey, model, objects);
    const open = (item: DrawingItem, event?: React.MouseEvent) => {
        const path = pathOf(item);
        if (path) openPath(path, event);
    };

    const drawing = view.image && view.imageKey ? (
        <BlueprintWithMarkers
            key={`${drawingId(view)}-${view.imageKey}`}
            blueprint={view.image}
            currentBlueprintKey={view.imageKey}
            building={building}
            rooms={markers.rooms}
            utilities={markers.utilities}
            spots={markers.spots}
            onRoomClick={(key, event) => open({ kind: 'room', key }, event)}
            onUtilityClick={(key, event) => open({ kind: 'component', key }, event)}
            onSpotClick={(key, event) => open({ kind: markers.spotKinds.get(key) ?? 'spot', key }, event)}
            onRoomHover={key => setHoveredMarker(key ? { kind: 'room', key } : null)}
            onUtilityHover={key => setHoveredMarker(key ? { kind: 'component', key } : null)}
            onSpotHover={key => setHoveredMarker(key ? { kind: markers.spotKinds.get(key) ?? 'spot', key } : null)}
            highlight={highlight}
            compactMarkers={view.kind === 'satellite'}
            hideTitle
        />
    ) : (
        <div className="bt-empty">
            <p>Ingen ritning uppladdad.</p>
            {pagePath && <Link className="btn btn-outline-primary btn-sm bt-empty-link" to={pagePath.path}>{pagePath.label}</Link>}
        </div>
    );

    const header = (
        <div className="bt-drawing-header">
            <div className="bt-pills" role="tablist" aria-label="Ritning">
                {views.map(v => (
                    <button
                        key={drawingId(v)}
                        type="button"
                        role="tab"
                        aria-selected={drawingId(v) === drawingId(view)}
                        className={`bt-pill${drawingId(v) === drawingId(view) ? ' bt-pill--active' : ''}${v.image ? '' : ' bt-pill--no-image'}`}
                        onClick={() => onViewChange(drawingId(v))}
                        title={v.image ? undefined : 'Ingen ritning uppladdad'}
                    >
                        {v.label}
                    </button>
                ))}
            </div>
            <div className="bt-layers">
                {layers.map(layer => (
                    <label key={layer.key}>
                        <ToggleSwitch checked={shows(layer.key)} onChange={() => toggleLayer(layer.key)} />
                        <span>{layer.label}</span>
                    </label>
                ))}
                {pagePath && view.image && <Link className="btn btn-outline-primary btn-sm bt-page-link" to={pagePath.path}>{pagePath.label}</Link>}
            </div>
        </div>
    );

    const list = groups.length === 0 ? (
        <p className="bt-muted">Inget att visa på den här ritningen.</p>
        ) : (
        <div className="bt-item-list" onMouseLeave={() => setHovered(null)}>
            {groups.map(group => (
                <section key={group.key}>
                    <h5 className="bt-group-title">
                        {group.label} <span className="bt-count">{group.items.length}</span>
                    </h5>
                    <ul>
                        {group.items.map(({ item, label }) => {
                            const path = pathOf(item);
                            const hover = () => setHovered(group.key === 'unplaced' ? null : item);
                            const rowClass = `bt-item${hoveredMarker && group.key !== 'unplaced' && itemId(hoveredMarker) === itemId(item) ? ' bt-item--marked' : ''}`;
                            const rowContent = (
                                <>
                                    <span className="bt-item-label">{label}</span>
                                    <ChevronRight className="bt-item-chevron" size={16} aria-hidden="true" />
                                </>
                            );
                            return (
                                <li key={itemId(item)} data-item={group.key === 'unplaced' ? undefined : itemId(item)}>
                                    {path ? (
                                        <Link to={path} className={rowClass} onMouseEnter={hover} onFocus={hover}>
                                            {rowContent}
                                        </Link>
                                    ) : (
                                        <button type="button" className={rowClass} onMouseEnter={hover} onFocus={hover}>
                                            {rowContent}
                                        </button>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </section>
            ))}
        </div>
    );

    return (
        <>
            {header}
            <div className={`bt-layout${view.image && view.imageKey ? '' : ' bt-layout--flow'}`}>
                <div className="bt-main">{drawing}</div>
                <aside className="bt-pane">
                    <div className="bt-pane-card" ref={paneRef}>{list}</div>
                </aside>
            </div>
        </>
    );
};

export default DrawingsTab;
